import { QueryKey } from "@tanstack/react-query";

import type { FeedbackFrontend } from "@performyard/service-contracts";
import { JSON_STREAM_DELIMITER } from "@performyard/utils-isomorphic";

import { queryClient } from "../../../queryClient";
import internalRequest, { internalStreamRequest, processJsonStream } from "./internal-base-request";

export type CompanyFeedbackStreamFilters = {
	authorId?: string;
	subjectId?: string;
	sharedWith?: string;
	categories?: string[];
	dateAfter?: string;
	dateBefore?: string;
	searchTerm?: string;
	customFieldIds?: string[];
	responseToRequest?: boolean;
};

export type CompanyFeedbackCustomField = {
	_id: string;
	name: string;
	type: string;
};

export type CompanyFeedbackStreamResult = {
	feedbacks: FeedbackFrontend[];
	customFields: CompanyFeedbackCustomField[];
};

// Track active stream
const activeStreams = new Map<string, AbortController>();

export const companyFeedbackStream = async (
	queryKey: QueryKey,
	filters?: CompanyFeedbackStreamFilters,
	onChunk?: (chunk: { data: FeedbackFrontend[] }) => void
) => {
	// Create a unique identifier for this request that we'll use to clean up
	const streamKey = Math.random().toString(36).slice(2);

	// Abort all existing streams before starting a new one
	for (const [key, controller] of activeStreams) {
		controller.abort();
		activeStreams.delete(key);
	}

	const abortController = new AbortController();
	activeStreams.set(streamKey, abortController);

	try {
		const params = new URLSearchParams();
		if (filters?.authorId) params.set("authorId", filters.authorId);
		if (filters?.subjectId) params.set("subjectId", filters.subjectId);
		if (filters?.sharedWith) params.set("sharedWith", filters.sharedWith);
		if (filters?.categories?.length) params.set("categories", filters.categories.join(","));
		if (filters?.dateAfter) params.set("dateAfter", filters.dateAfter);
		if (filters?.dateBefore) params.set("dateBefore", filters.dateBefore);
		if (filters?.searchTerm) params.set("searchTerm", filters.searchTerm);
		if (filters?.customFieldIds?.length) params.set("customFieldIds", filters.customFieldIds.join(","));
		if (filters?.responseToRequest !== undefined)
			params.set("responseToRequest", String(filters.responseToRequest));
		const qs = params.toString();
		const url = `/v1/feedback${qs ? `?${qs}` : ""}`;

		const response = await internalStreamRequest(url, {
			method: "GET",
			signal: abortController.signal,
		});

		if (response.status !== 200) {
			throw new Error(`Failed to get feedback: ${response.statusText}`);
		}

		let customFields: CompanyFeedbackCustomField[] = [];

		await processJsonStream<FeedbackFrontend>({
			response,
			delimiter: JSON_STREAM_DELIMITER,
			signal: abortController.signal,
			onItem: (parsed) => {
				if (parsed.type === "metadata") {
					customFields = parsed.customFields;
					return null;
				}
				return parsed;
			},
			onParsed: (parsedData) => {
				onChunk?.({ data: parsedData });
				queryClient.setQueryData(queryKey, (current: CompanyFeedbackStreamResult) => ({
					feedbacks: [...(current?.feedbacks ?? []), ...parsedData],
					customFields: customFields,
				}));
			},
		});

		return queryClient.getQueryData<CompanyFeedbackStreamResult>(queryKey) ?? { feedbacks: [], customFields: [] };
	} catch (error: any) {
		if (error.name === "AbortError") {
			return { feedbacks: [], customFields: [] };
		}
		throw error;
	} finally {
		activeStreams.delete(streamKey);
	}
};

/** POST /v1/feedback/request/bulk — enqueues an async bulk feedback-request job. Returns the task id. */
export const requestFeedbackBulk = async (props: { recipientIds: string[]; message?: string }) => {
	const [data] = await internalRequest<{ taskId: string }>("/v1/feedback/request/bulk", {
		method: "POST",
		body: JSON.stringify({
			recipientIds: props.recipientIds,
			...(props.message ? { message: props.message } : {}),
		}),
	});
	return data;
};

/** POST /v1/feedback/bulk — enqueues an async bulk give-feedback job. Returns the task id. */
export const createFeedbackBulk = async (props: {
	subjectIds: string[];
	sharedWith: string;
	content: string;
	feedbackMessage: string;
	feedbackRequestId?: string;
}) => {
	const [data] = await internalRequest<{ taskId: string }>("/v1/feedback/bulk", {
		method: "POST",
		body: JSON.stringify({
			subjectIds: props.subjectIds,
			sharedWith: props.sharedWith,
			content: props.content,
			feedbackMessage: props.feedbackMessage,
			...(props.feedbackRequestId ? { feedbackRequestId: props.feedbackRequestId } : {}),
		}),
	});
	return data;
};
